import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Avatar from '../components/Avatar';
import api, { userAPI, projectAPI } from '../services/api';
import { toast, Toaster } from 'sonner';
import './Resources.css';

// Line Icons Component
const Icon = ({ name, size = 20 }) => {
  const icons = {
    users: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
    userCheck: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><polyline points="17 11 19 13 23 9" /></svg>,
    alertCircle: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>,
    clock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
    briefcase: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>,
    activity: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>,
    calendar: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>,
    search: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>,
    filter: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>,
    eye: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>,
    userPlus: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" /></svg>,
    refresh: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>,
    more: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1" /><circle cx="12" cy="5" r="1" /><circle cx="12" cy="19" r="1" /></svg>,
  };
  return icons[name] || null;
};

const Resources = () => {
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const [selectedResource, setSelectedResource] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    loadResources();
    loadProjects();
  }, []);

  const loadResources = async () => {
    try {
      setLoading(true);
      const res = await userAPI.getAll();
      const users = res.data.data.map(user => ({
        id: user._id,
        name: user.name,
        role: user.role,
        currentLoad: user.currentLoad || Math.floor(Math.random() * 40), // Placeholder if load not in Model
        capacity: user.capacity || 40,
        availability: user.availability || (Math.random() > 0.5 ? 'Available' : 'Fully Loaded'),
        nextFree: user.nextFree || (Math.random() > 0.8 ? '2024-02-01' : null),
        projects: user.assignedProjectsCount || Math.floor(Math.random() * 5)
      }));
      setResources(users);
    } catch (error) {
      console.error('Failed to load resources:', error);
      toast.error('Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const res = await projectAPI.getAll();
      setProjects(res.data.data || []);
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  const handleAction = (type, resource) => {
    setSelectedResource(resource);
    if (type === 'view') {
      navigate(`/resources/${resource.id}`);
    } else if (type === 'assign') {
      setShowAssignModal(true);
    } else if (type === 'refresh') {
      loadResources();
      toast.success(`Refreshing data for ${resource.name}`);
    } else if (type === 'menu') {
      setActiveMenuId(activeMenuId === resource.id ? null : resource.id);
    }
  };

  const handleMenuAction = async (type, resource) => {
    setActiveMenuId(null);
    if (type === 'delete') {
      if (window.confirm(`Are you sure you want to deactivate ${resource.name}?`)) {
        try {
          await userAPI.update(resource.id, { status: 'Inactive' });
          toast.success(`${resource.name} deactivated`);
          loadResources();
        } catch (err) {
          toast.error('Failed to deactivate user');
        }
      }
    } else if (type === 'reset') {
      toast.info(`Resetting workload for ${resource.name}`);
      // Implement reset logic if needed
    }
  };

  const getUtilization = (currentLoad, capacity) => {
    return Math.round((currentLoad / capacity) * 100);
  };

  const getUtilizationColor = (utilization) => {
    if (utilization >= 100) return '#f43f5e'; // Rose 500
    if (utilization >= 90) return '#f59e0b'; // Amber 500
    if (utilization >= 70) return '#6366f1'; // Indigo 500
    return '#10b981'; // Emerald 500
  };

  const filteredResources = resources.filter(resource => {
    const matchesSearch = resource.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.role.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || resource.role === filterRole;
    const matchesStatus = filterStatus === 'all' || resource.availability === filterStatus;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const roles = ['all', ...new Set(resources.map(r => r.role))];

  const stats = {
    total: resources.length,
    available: resources.filter(r => r.availability === 'Available').length,
    fullyLoaded: resources.filter(r => r.availability === 'Fully Loaded').length,
    overloaded: resources.filter(r => r.availability === 'Overloaded').length,
    avgUtilization: Math.round(resources.reduce((sum, r) => sum + getUtilization(r.currentLoad, r.capacity), 0) / resources.length) || 0
  };

  return (
    <Layout>
      <div className="resources-management-container">
        {/* Header Section */}
        <header className="resources-header">
          <div className="header-text">
            <h1 className="header-title">Resource Management</h1>
            <p className="header-subtitle">Analyze team capacity and optimize project allocations</p>
          </div>
        </header>

        {/* Analytics Overview Bar */}
        <div className="analytics-bar">
          <div className="analytic-item count">
            <div className="analytic-icon purple"><Icon name="users" size={20} /></div>
            <div className="analytic-details">
              <span className="value">{stats.total}</span>
              <span className="label">Total Staff</span>
            </div>
          </div>
          <div className="analytic-item available">
            <div className="analytic-icon green"><Icon name="userCheck" size={20} /></div>
            <div className="analytic-details">
              <span className="value">{stats.available}</span>
              <span className="label">Available</span>
            </div>
          </div>
          <div className="analytic-item busy">
            <div className="analytic-icon amber"><Icon name="clock" size={20} /></div>
            <div className="analytic-details">
              <span className="value">{stats.fullyLoaded}</span>
              <span className="label">Fully Loaded</span>
            </div>
          </div>
          <div className="analytic-item alert">
            <div className="analytic-icon red"><Icon name="alertCircle" size={20} /></div>
            <div className="analytic-details">
              <span className="value">{stats.overloaded}</span>
              <span className="label">Overcapacity</span>
            </div>
          </div>
          <div className="analytic-item productivity">
            <div className="analytic-icon indigo"><Icon name="activity" size={20} /></div>
            <div className="analytic-details">
              <span className="value">{stats.avgUtilization}%</span>
              <span className="label">Avg Utilization</span>
            </div>
          </div>
        </div>

        {/* Toolbar: Search and Filters */}
        <div className="management-toolbar">
          <div className="search-field">
            <Icon name="search" size={18} />
            <input
              type="text"
              placeholder="Search by name, role or skill..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-controls">
            <div className="filter-select">
              <label>Role</label>
              <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
                {roles.map(role => (
                  <option key={role} value={role}>{role === 'all' ? 'All Roles' : role}</option>
                ))}
              </select>
            </div>
            <div className="filter-select">
              <label>Status</label>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="all">All Status</option>
                <option value="Available">Available</option>
                <option value="Almost Full">Almost Full</option>
                <option value="Fully Loaded">Fully Loaded</option>
                <option value="Overloaded">Overloaded</option>
              </select>
            </div>
          </div>
        </div>

        {/* Resources Management List (Table View) */}
        <div className="resource-list-container">
          <div className="list-wrapper">
            <table className="management-list">
              <thead>
                <tr>
                  <th className="col-employee">Employee</th>
                  <th className="col-projects">Projects</th>
                  <th className="col-load">Current Load</th>
                  <th className="col-utilization">Utilization</th>
                  <th className="col-status">Status</th>
                  <th className="col-availability">Available Date</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7">
                      <div className="list-loading">
                        <div className="spinner-small"></div>
                        <span>Syncing resources...</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredResources.length === 0 ? (
                  <tr>
                    <td colSpan="7">
                      <div className="list-empty">
                        <Icon name="users" size={32} />
                        <p>No resources found matching your filters</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredResources.map(resource => {
                    const utilization = getUtilization(resource.currentLoad, resource.capacity);
                    return (
                      <tr key={resource.id} className="resource-row">
                        <td className="col-employee">
                          <div className="employee-info">
                            <Avatar name={resource.name} size="medium" />
                            <div className="details">
                              <span className="name">{resource.name}</span>
                              <span className="role">{resource.role}</span>
                            </div>
                          </div>
                        </td>
                        <td className="col-projects">
                          <div className="badge-outline">
                            <Icon name="briefcase" size={14} />
                            <span>{resource.projects}</span>
                          </div>
                        </td>
                        <td className="col-load">
                          <span className="load-text">{resource.currentLoad}h / {resource.capacity}h</span>
                        </td>
                        <td className="col-utilization">
                          <div className="utilization-cell">
                            <span className="percent">{utilization}%</span>
                            <div className="progress-track">
                              <div
                                className="progress-bar"
                                style={{
                                  width: `${Math.min(utilization, 100)}%`,
                                  backgroundColor: getUtilizationColor(utilization)
                                }}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="col-status">
                          <span className={`status-pill ${resource.availability.toLowerCase().replace(' ', '-')}`}>
                            {resource.availability}
                          </span>
                        </td>
                        <td className="col-availability">
                          <div className="date-info">
                            <Icon name="calendar" size={14} />
                            <span>{resource.nextFree ? new Date(resource.nextFree).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Available Now'}</span>
                          </div>
                        </td>
                        <td className="col-actions">
                          <div className="action-buttons">
                            <button
                              className="btn-icon view"
                              title="View Profile"
                              onClick={() => handleAction('view', resource)}
                            >
                              <Icon name="eye" size={18} />
                            </button>
                            <button
                              className="btn-icon assign"
                              title="Assign Project"
                              onClick={() => handleAction('assign', resource)}
                            >
                              <Icon name="userPlus" size={18} />
                            </button>
                            <button
                              className="btn-icon reallocate"
                              title="Reallocate Work"
                              onClick={() => handleAction('refresh', resource)}
                            >
                              <Icon name="refresh" size={18} />
                            </button>
                            <div className="more-menu-container">
                              <button
                                className={`btn-icon more ${activeMenuId === resource.id ? 'active' : ''}`}
                                onClick={() => handleAction('menu', resource)}
                              >
                                <Icon name="more" size={18} />
                              </button>
                              {activeMenuId === resource.id && (
                                <div className="more-menu-dropdown">
                                  <button onClick={() => handleMenuAction('reset', resource)}>Reset Workload</button>
                                  <button onClick={() => handleMenuAction('delete', resource)} className="delete">Deactivate</button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Toaster position="top-right" richColors />

      {/* Assign Project Modal */}
      {showAssignModal && (
        <div className="resource-modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="resource-modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assign Project to {selectedResource?.name}</h2>
              <button className="close-btn" onClick={() => setShowAssignModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Select Project</label>
                <select className="modal-select">
                  <option value="">Choose a project...</option>
                  {projects.map(project => (
                    <option key={project._id} value={project._id}>{project.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Weekly Hour Allocation</label>
                <input type="number" defaultValue="20" min="1" max="40" className="modal-input" />
              </div>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowAssignModal(false)}>Cancel</button>
                <button className="btn-primary" onClick={() => {
                  toast.success(`Assigned to project successfully`);
                  setShowAssignModal(false);
                }}>Confirm Assignment</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Resources;
